// @ts-strict-ignore
/** This file contains functions that convert between in-memory query format and bookmark format */

import pick from 'lodash/pick';

import {SESSION_EVENTS} from 'common/report/constants';
import {ArrayType} from 'common/util/typescript';
import {renameEvent} from 'common/report/util/events';
import {
  BookmarkFunnelStep,
  BookmarkOrStep,
  FunnelBookmark,
  isLegacyAggregateBy,
  isOrStep,
  LegacyBookmarkFunnelStep,
} from 'common/report/funnels/models/types';
import {convertSegfilterToPropertyFilter, SegFilterProps} from 'common/widgets/property-filter-menu/models/segfilter';
import {FunnelAttribution} from 'common/query-builder/query-builder/types';
import {
  FiltersOperator,
  FlowsQueryEventEntry,
  QueryEventEntry,
  QueryGroupedEntry,
  SelectorTypeDefinitions,
} from 'common/query-builder/query-entry-section/types';
import {Segmentation} from 'common/types/reports/query';
import {eventDefinitionsStore} from 'common/report/stores/event-definitions';
import {PropertyType, ResourceType} from 'common/report/constants';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';

export function mapBookmarkToHoldPropConstant(aggregateBy: FunnelBookmark[`aggregate_by`]): Segmentation[] {
  return aggregateBy.map((item) => {
    if (isLegacyAggregateBy(item)) {
      return {
        propertyName: item.raw,
        resourceType: ResourceType.Event,
        propertyType: PropertyType.String,
        propertyDefaultType: PropertyType.String,
      };
    } else {
      return item;
    }
  });
}

export function mapBookmarkToAttribution(actionMode: string, actionStep: number): FunnelAttribution {
  if (actionMode === `step`) {
    return {action_mode: actionMode, action_step: actionStep};
  } else if (actionMode === `first_touch` || actionMode === `last_touch`) {
    return {action_mode: actionMode};
  } else {
    return {action_mode: `last_touch`};
  }
}

export function mapBookmarkExclusionStepToEventEntry(
  eventCount: number,
  step: FunnelBookmark[`exclusions`][number],
): QueryEventEntry {
  const entry = mapBookmarkStepToEventEntry(step as any); // as any can be removed when FiltersOperator updated to use generated definition

  const {from = 0, to = 0} = step.steps;
  let excludeRange, excludeAll;

  if (from === 1 && to === eventCount) {
    excludeAll = true;
    excludeRange = {};
  } else {
    excludeAll = false;
    excludeRange = {from, to};
  }

  return {
    ...entry,
    excludeRange,
    excludeAll,
  };
}

/** Deserialize bookmark steps, taking grouped steps into account. */
export function mapBookmarkStepsToEntries(steps: FunnelBookmark[`steps`]): Array<QueryEventEntry | QueryGroupedEntry> {
  return steps.map((step) => {
    if (isOrStep(step)) {
      return mapBookmarkOrStepToGroupedEntry(step);
    } else {
      return mapBookmarkStepToEventEntry(step);
    }
  });
}

export function mapBookmarkOrStepToGroupedEntry(step: BookmarkOrStep): QueryGroupedEntry {
  return {
    type: SelectorTypeDefinitions.GroupedEvent,
    event: step.compare.map(mapBookmarkStepToEventEntry),
    filters: [],
  };
}

// TODO opslead
export function mapBookmarkStepToEventEntry(step: LegacyBookmarkFunnelStep): QueryEventEntry {
  const additionalAttributes: Partial<QueryEventEntry> = {};
  const isCustomEvent = Object.prototype.hasOwnProperty.call(step, `custom_event`);

  let stepFilters = [];
  if (`property_filter_params_list` in step) {
    stepFilters = step.property_filter_params_list;
  } else if (step.filter) {
    // Older bookmarks don't have property filter params list,
    // instead they have just one filter at the top level.
    const stepFilter = pick(step, SegFilterProps);
    if (typeof stepFilter.property === `string`) {
      // Some older bookmarks have even less information that needs to be translated.
      stepFilter.property = {
        name: stepFilter.property,
        type: stepFilter.type,
        source: `properties`,
      };
    }
    stepFilters = [stepFilter];
  }

  const filters = stepFilters.map(convertSegfilterToPropertyFilter);
  const flowsDirections = pick(step, `forward`, `reverse`);

  // determine label of the event
  // older funnels bookmarks don't store all the necessary information in them. So we need to guess what the current
  // non renamed label would be, and we consider it renamed if the stored label doesn't match that.
  let eventLabel = step.step_label || ``;
  const sessionEventKey =
    step.session_event &&
    Object.keys(SESSION_EVENTS).find((session) => SESSION_EVENTS[session][`session_event`] === step.session_event);

  let eventName;
  const collectEverythingBlob = {};
  let ignoreRenamedEventCheck = false;

  if (step.custom_event) {
    // prevent renamed event name check since we rely on store to be fetched
    // if store isn't fetched - we erroneously assume there has been unsaved changes. (SRFE 4228)
    ignoreRenamedEventCheck = !eventDefinitionsStore.hasFetchedValue();
    eventName = renameEvent(step.custom_event_name) || `Deleted Custom Event`;
    /**
     * TODO: step_label is or was being persisted with the name of custom events, the renaming functionality is
     * brittle in Funnels since we don't have a field specifying user intent (renaming) for persisting a renamed
     * label, this needs to be removed when:
     * 1. We make the appropriate changes on query-api to stop relying on step_label being persisted
     *   - Always rely on renaming the event value with renameEvent function whenever we display event data to the user
     */
    eventLabel = eventLabel.startsWith(`$custom_event:`) ? eventName : eventLabel;
  } else if (sessionEventKey) {
    eventName = renameEvent(sessionEventKey);
  } else if (!step.event) {
    eventName = eventLabel;
  } else {
    eventName = renameEvent(step.event);
  }

  let eventId = {};
  if (isCustomEvent) {
    eventId = {id: step.custom_event};
  } else if (`event_id` in step) {
    eventId = {id: step.event_id};
  }

  // also check against step.event for older bookmarks before we performed renaming for newer properties.
  const isRenamed = eventLabel !== step.event && eventLabel !== eventName;
  const filtersOperator = `bool_op` in step ? step.bool_op : FiltersOperator.Or;

  return {
    ...additionalAttributes,
    ...flowsDirections,
    filters,
    type: ResourceType.Event,
    filtersOperator,
    event: {
      ...eventId,
      ...collectEverythingBlob,
      custom: isCustomEvent,
      label: eventName,
      value: sessionEventKey || step.event || step.custom_event_name || null,
      ...(!ignoreRenamedEventCheck && isRenamed ? {renamed: eventLabel} : {}),
    },
  };
}

export function mapHoldPropConstantToBookmark(holdingPropertyConstant: Segmentation[]) {
  return holdingPropertyConstant;
}

export function mapEventEntryToBookmarkExclusionStep(
  eventsLength: number,
  step: QueryEventEntry,
): ArrayType<FunnelBookmark[`exclusions`]> {
  const entry = mapEventEntryToBookmarkStep(step);

  let steps;
  if (step.excludeRange && `from` in step.excludeRange) {
    steps = {...step.excludeRange};
  } else if (step.excludeAll) {
    steps = {from: 1, to: eventsLength};
  }

  return {
    ...entry,
    steps,
  } as any; // as any can be removed when FiltersOperator updated to use generated definition
}

export function mapGroupedEventToBookmarkStep(step: QueryGroupedEntry): BookmarkOrStep {
  return {
    compare: step.event.map(mapEventEntryToBookmarkStep),
  };
}

export function mapEventEntryToBookmarkStep(step: QueryEventEntry | FlowsQueryEventEntry): BookmarkFunnelStep {
  const {event, filters: stepFilters, filtersOperator} = step;
  const filters = stepFilters.map((filter) => (filter instanceof PropertyFilter ? filter.getAttributes() : filter));
  let eventProperties: Partial<FunnelBookmark[`steps`][number]> = {
    step_label: event.renamed || event.label.toString(),
    ...pick(step, `forward`, `reverse`),
  };

  // generate event properties
  if (event.custom) {
    eventProperties = {
      ...eventProperties,
      custom_event: event.id,
      custom_event_name: event.value,
    };
  } else if (SESSION_EVENTS[event.value]) {
    eventProperties = {
      ...SESSION_EVENTS[event.value],
      ...eventProperties,
    };
  } else {
    eventProperties = {
      ...eventProperties,
      event: event.value,
      ...(event.id ? {event_id: event.id} : {}),
    };
  }

  return {
    ...eventProperties,
    bool_op: filtersOperator,
    property_filter_params_list: filters,
  };
}
